import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { RefreshCw, Loader2, Power, Zap, Pause, Play } from 'lucide-react';
import { adminApi, ApiError } from '../../lib/api';

const ControlsTab: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [apiControls, setApiControls] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, ctrlRes] = await Promise.all([
        adminApi.apiStatus(),
        adminApi.apiControls(),
      ]);
      setStatus(statusRes);
      setApiControls(ctrlRes);
    } catch (e) {
      console.error('Controls fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const doAction = async (path: string, method = 'POST', body?: any) => {
    setActionLoading(path);
    setMsg(null);
    try {
      const res = await adminApi.configAction(path, { method, body: body ? JSON.stringify(body) : undefined });
      setMsg((res as any)?.message || 'Action completed successfully');
      await fetchStatus();
    } catch (e: any) {
      setMsg(e instanceof ApiError ? e.detail : e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const openPositions =
    status?.open_positions ??
    status?.broker_open_positions ??
    status?.managed_open_positions ??
    0;

  const toggleApiControl = async (key: string, currentValue: boolean) => {
    const newValue = !currentValue;
    if (key === 'position_monitoring_enabled' && !newValue && Number(openPositions) > 0) {
      setMsg(`Cannot disable position monitoring while ${openPositions} position(s) are open.`);
      return;
    }
    await doAction(`/api/titan/config/api-controls?${key}=${newValue}`, 'PUT');
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const killActive = status?.kill_switch_active || status?.circuit_breaker?.status === 'HALTED';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Emergency controls and API management</p>
        <Button variant="outline" size="sm" onClick={fetchStatus}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {msg && <Alert className="bg-blue-500/10 border-blue-500/30 text-blue-600"><AlertDescription>{msg}</AlertDescription></Alert>}

      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Emergency Controls</h3>
        <div className="flex flex-wrap gap-2">
          <Button variant="destructive" size="sm" onClick={() => doAction('/api/kill-switch', 'POST')}
            disabled={actionLoading === '/api/kill-switch' || killActive}>
            {actionLoading === '/api/kill-switch' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Power className="w-4 h-4 mr-1" />}
            Kill Switch
          </Button>
          <Button variant="outline" size="sm" onClick={() => doAction('/api/kill-switch/reset', 'POST')}
            disabled={actionLoading === '/api/kill-switch/reset' || !killActive}>
            {actionLoading === '/api/kill-switch/reset' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
            Reset Kill Switch
          </Button>
          <Button variant="outline" size="sm" onClick={() => doAction('/api/pause', 'POST')} disabled={!!actionLoading}>
            <Pause className="w-4 h-4 mr-1" /> Pause
          </Button>
          <Button variant="outline" size="sm" onClick={() => doAction('/api/resume', 'POST')} disabled={!!actionLoading}>
            <Play className="w-4 h-4 mr-1" /> Resume
          </Button>
        </div>
        {killActive && (
          <p className="text-xs text-red-600 mt-2">Kill switch is ACTIVE — all trading halted</p>
        )}
      </Card>

      {apiControls && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">API Cost Controls</h3>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="text-xs h-7"
                onClick={() => doAction('/api/config/api-controls/disable-all-scanners', 'POST')} disabled={!!actionLoading}>
                Disable Scanners
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-7"
                onClick={() => doAction('/api/config/api-controls/enable-all', 'POST')} disabled={!!actionLoading}>
                Enable All
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(apiControls).filter(([k]) => k.endsWith('_enabled')).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between py-2 px-3 rounded bg-muted/30">
                <span className="text-xs">{key.replace(/_enabled$/, '').replace(/_/g, ' ')}</span>
                <Button variant={val ? 'default' : 'outline'} size="sm" className="text-xs h-6 px-2"
                  onClick={() => toggleApiControl(key, !!val)} disabled={!!actionLoading}>
                  {val ? 'ON' : 'OFF'}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ControlsTab;
